import React, { useRef, useState } from 'react';
import { wikiAPI } from '../services/api';
import { useNotification } from '../hooks/useNotification';

const WikiEditor = ({ content, onChange, onSave, onCancel, isEditing }) => {
  const { showSuccess, showError } = useNotification();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showError('Invalid file type. Please upload an image (jpg, png, gif, webp)');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      showError('File too large. Maximum size is 10MB');
      return;
    }

    try {
      setUploading(true);
      const response = await wikiAPI.uploadImage(file);
      const imageUrl = wikiAPI.getImageUrl(response.data.image_id);
      
      // Insert markdown image syntax at cursor position
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = content || '';
      const before = text.substring(0, start);
      const after = text.substring(end);
      const imageMarkdown = `![${file.name}](${imageUrl})`;
      const newContent = before + imageMarkdown + after;
      
      onChange(newContent);
      
      // Reset cursor position after image markdown
      setTimeout(() => {
        const newPos = start + imageMarkdown.length;
        textarea.setSelectionRange(newPos, newPos);
        textarea.focus();
      }, 0);
      
      showSuccess('Image uploaded successfully');
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleInsertImage = () => {
    fileInputRef.current?.click();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleInsertImage}
            disabled={uploading}
            style={{ fontSize: '12px' }}
          >
            {uploading ? 'Uploading...' : 'Insert Image'}
          </button>
        </div>
        {isEditing && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary" onClick={onSave}>Save</button>
            <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleImageUpload}
        style={{ display: 'none' }}
      />
      <textarea
        ref={textareaRef}
        value={content || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Write content in Markdown..."
        rows={15}
        className="form-textarea"
        style={{ width: '100%', fontFamily: 'monospace', fontSize: '14px' }}
      />
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
        Markdown supported. Use the "Insert Image" button to upload images.
      </div>
    </div>
  );
};

export default WikiEditor;

